import { useState, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { PdfThumbnail } from './PdfThumbnail';
import { FileTypeIcon } from './FileTypeIcon';

interface MediaPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
}

interface PreviewFile {
  file: File;
  preview: string;
  id: string;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export const MediaPicker: React.FC<MediaPickerProps> = ({
  isOpen,
  onClose,
  onFilesSelected,
  maxFiles = 5,
}) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<PreviewFile[]>([]);
  const [warning, setWarning] = useState<string | null>(null);
  const [cameraAvailable, setCameraAvailable] = useState(false);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Check camera availability
  const checkCamera = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some((device) => device.kind === 'videoinput');
      setCameraAvailable(hasCamera);
    } catch {
      setCameraAvailable(false);
    }
  }, []);

  // Check on mount
  useState(() => {
    if (isOpen) {
      checkCamera();
    }
  });

  const handleFileSelection = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const newFiles: PreviewFile[] = [];
      let hasWarning = false;

      Array.from(files).forEach((file) => {
        if (selectedFiles.length + newFiles.length >= maxFiles) {
          setWarning(t('messaging.media.maxFilesReached', { max: maxFiles }));
          hasWarning = true;
          return;
        }

        if (file.size > MAX_FILE_SIZE) {
          setWarning(
            t('messaging.media.fileTooLarge', {
              name: file.name,
              max: '50MB',
            })
          );
          hasWarning = true;
          return;
        }

        const preview =
          file.type.startsWith('image/') || file.type.startsWith('video/')
            ? URL.createObjectURL(file)
            : '';

        newFiles.push({
          file,
          preview,
          id: `${file.name}-${Date.now()}-${Math.random()}`,
        });
      });

      if (!hasWarning) {
        setWarning(null);
      }

      setSelectedFiles((prev) => [...prev, ...newFiles]);
    },
    [selectedFiles.length, maxFiles, t]
  );

  const removeFile = useCallback((id: string) => {
    setSelectedFiles((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      const removed = prev.find((f) => f.id === id);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
    setWarning(null);
  }, []);

  const handleSend = useCallback(() => {
    if (selectedFiles.length === 0) return;

    const files = selectedFiles.map((f) => f.file);
    onFilesSelected(files);

    selectedFiles.forEach((f) => {
      if (f.preview) {
        URL.revokeObjectURL(f.preview);
      }
    });

    setSelectedFiles([]);
    setWarning(null);
    onClose();
  }, [selectedFiles, onFilesSelected, onClose]);

  const handleClose = useCallback(() => {
    selectedFiles.forEach((f) => {
      if (f.preview) {
        URL.revokeObjectURL(f.preview);
      }
    });
    setSelectedFiles([]);
    setWarning(null);
    onClose();
  }, [selectedFiles, onClose]);

  if (!isOpen) return null;

  const hasFiles = selectedFiles.length > 0;

  return (
    <>
      {/* Transparent backdrop to catch outside clicks */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-40"
          />
        )}
      </AnimatePresence>

      {/* Compact upward dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="absolute bottom-full left-0 z-50 mb-2 w-56 origin-bottom-left"
          >
            <div className="overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-black/5">
              {/* Menu options */}
              <div className="py-1">
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 transition-colors hover:bg-slate-50"
                >
                  <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span className="font-medium">{t('messaging.media.photoVideo')}</span>
                </button>

                {cameraAvailable && (
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 transition-colors hover:bg-slate-50"
                  >
                    <svg className="h-5 w-5 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    <span className="font-medium">{t('messaging.media.camera')}</span>
                  </button>
                )}

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 transition-colors hover:bg-slate-50"
                >
                  <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span className="font-medium">{t('messaging.media.file')}</span>
                </button>
              </div>

              {/* Warning */}
              {warning && (
                <div className="border-t border-slate-100 px-4 py-2">
                  <p className="text-xs font-medium text-amber-600">{warning}</p>
                </div>
              )}

              {/* File previews */}
              {hasFiles && (
                <div className="border-t border-slate-100 p-3">
                  <p className="mb-2 text-xs font-medium text-neutral-500">
                    {t('messaging.media.selected')} ({selectedFiles.length}/{maxFiles})
                  </p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {selectedFiles.map((fileItem) => (
                      <div
                        key={fileItem.id}
                        className="relative aspect-square overflow-hidden rounded-lg bg-slate-100"
                      >
                        {fileItem.preview ? (
                          fileItem.file.type.startsWith('image/') ? (
                            <img
                              src={fileItem.preview}
                              alt={fileItem.file.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <video
                              src={fileItem.preview}
                              className="h-full w-full object-cover"
                            />
                          )
                        ) : fileItem.file.type === 'application/pdf' ? (
                          <div className="h-full w-full overflow-hidden">
                            <PdfThumbnail
                              url={URL.createObjectURL(fileItem.file)}
                              fileName={fileItem.file.name}
                              fileSize={fileItem.file.size}
                              isMine={false}
                            />
                          </div>
                        ) : (
                          <FileTypeIcon
                            fileName={fileItem.file.name}
                            fileSize={fileItem.file.size}
                            compact
                          />
                        )}
                        <button
                          onClick={() => removeFile(fileItem.id)}
                          className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 shadow-sm transition-colors hover:bg-red-600"
                        >
                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Send / Cancel */}
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={handleClose}
                      className="flex-1 rounded-lg border border-slate-200 py-1.5 text-xs font-medium text-neutral-600 transition-colors hover:bg-slate-50"
                    >
                      {t('messaging.media.cancel')}
                    </button>
                    <button
                      onClick={handleSend}
                      className="flex-1 rounded-lg bg-slate-700 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-slate-800"
                    >
                      {t('messaging.media.send')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden file inputs */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelection(e.target.files)}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFileSelection(e.target.files)}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="*"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelection(e.target.files)}
      />
    </>
  );
};
